import {
  Box,
  BoxProps,
  Flex,
  FlexProps,
  HStack,
  Link,
  LinkProps,
  Spinner,
  Text,
  TextProps,
  Tooltip,
  useColorModeValue,
} from "@chakra-ui/react";
import omit from "lodash/omit";
import { forwardRef, memo } from "react";
import { Link as RouterLink } from "react-router";

import { highlightStringMatch } from "@/common/components/Autocomplete/AutocompleteSelect";
import {
  CustomIconEmojiDisplay,
  IcoAutoFeedbackSurveys,
  IcoFeatureAutoCleanup,
  IcoFeatureCleanup,
  IconValueKey,
} from "@/common/components/Icons";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useFeatureParentNames } from "@/common/hooks/useFeatureParentNames";
import { IconSizesType } from "@/theme/Icon.theme";
import { ProcessingStatus } from "@reflag/shared/featureAPI";
import { FeatureSettingsUrl, FeatureUrl } from "@reflag/shared/urls";

const sizingPropMap: Record<
  IconSizesType,
  Record<"container" | "archivedLabel", any>
> = {
  xs: {
    container: {
      fontSize: "xs",
      fontWeight: "medium",
      gap: 1.5,
    },
    archivedLabel: {
      fontSize: "xs",
    },
  },
  sm: {
    container: {
      fontSize: "sm",
      fontWeight: "medium",
      gap: 1.5,
    },
    archivedLabel: {
      fontSize: "xs",
    },
  },
  md: {
    container: {
      fontSize: "md",
      fontWeight: "medium",
      gap: 2,
    },
    archivedLabel: {
      fontSize: "sm",
    },
  },
  lg: {
    container: {
      fontSize: "lg",
      fontWeight: "medium",
      gap: 2,
    },
    archivedLabel: {
      fontSize: "md",
    },
  },
  xl: {
    container: {
      fontSize: "xl",
      fontWeight: "medium",
      gap: 2,
    },
    archivedLabel: {
      fontSize: "lg",
    },
  },
  "2xl": {
    container: {
      fontSize: "xl",
      fontWeight: "medium",
      gap: 2,
    },
    archivedLabel: {
      fontSize: "lg",
    },
  },
} as const;

type FeatureDisplaySize = keyof typeof sizingPropMap;
type FeatureDisplayProps = {
  feature: {
    id: string;
    name: string;
    icon: string | null;
    iconColor: string | null;
    isArchived?: boolean;
    isStale?: boolean;
    processingStatus?: ProcessingStatus;
    parentFeatureId?: string | null;
    autoFeedbackSurveysEnabled?: boolean;
    hasCleanupPr?: boolean;
  };
  size?: FeatureDisplaySize;
  highlightText?: string;
  iconSize?: IconSizesType;
  includeParents?: boolean;
  subsegmentId?: string;
  showArchivedLabel?: boolean;
} & (
  | ({ link: true } & Omit<LinkProps, "size">)
  | ({ link?: false | undefined } & BoxProps)
);

export const FeatureDisplay = memo(
  forwardRef<HTMLElement, FeatureDisplayProps>(
    (
      {
        feature,
        size = "md",
        highlightText,
        iconSize = size,
        includeParents,
        subsegmentId,
        showArchivedLabel = true,
        ...props
      },
      ref,
    ) => {
      const { env } = useEnvironment();
      const commonFlexProps = {
        display: "flex",
        alignItems: "center",
        minW: 0,
        gap: 1,
      };

      const sizingProps = sizingPropMap[size];

      const content = (
        <>
          <CustomIconEmojiDisplay
            color={feature.iconColor}
            flexShrink={0}
            iconKey={feature.icon as IconValueKey}
            size={iconSize}
          />
          <FeatureNameDisplay
            archivedLabelProps={sizingProps.archivedLabel}
            feature={feature}
            flex="0 1 auto"
            highlightText={highlightText}
            includeParents={includeParents}
            showArchivedLabel={showArchivedLabel}
          />

          {feature.processingStatus === "bootstrapping" && (
            <Spinner
              flexShrink={0}
              ml={2}
              mr="auto"
              size="xs"
              variant="subtle"
            />
          )}
        </>
      );

      if (props.link) {
        return (
          <Link
            ref={ref}
            as={RouterLink}
            to={FeatureUrl(env, feature, {
              subsegment: subsegmentId,
            })}
            variant="unstyled"
            {...commonFlexProps}
            {...sizingProps.container}
            {...omit(props, "link")}
          >
            {content}
          </Link>
        );
      }

      return (
        <Box
          ref={ref}
          {...commonFlexProps}
          {...sizingProps.container}
          {...omit(props, "link")}
        >
          {content}
        </Box>
      );
    },
  ),
);

FeatureDisplay.displayName = "FeatureDisplay";

export function FeatureNameDisplay({
  feature,
  includeParents = true,
  highlightText,
  showArchivedLabel = true,
  archivedLabelProps = {},
  ...flexProps
}: FlexProps & { archivedLabelProps?: TextProps } & Pick<
    FeatureDisplayProps,
    "feature" | "includeParents" | "highlightText" | "showArchivedLabel"
  >) {
  const featureParentNames = useFeatureParentNames(feature.id);

  const featureName = highlightText
    ? highlightStringMatch(feature.name, highlightText)
    : feature.name;

  return (
    <Flex as="span" {...flexProps} display="flex" gap="1" minW="0">
      {includeParents
        ? featureParentNames.flatMap((p) => [
            <Box
              key={`parent-${feature.id}-${p.id}-name`}
              as="span"
              flex="0 99999 auto" // Set flex-shrink really high because parent names are less important than the flag name
              fontWeight="normal"
              isTruncated
            >
              {p.name}
            </Box>,
            <Box
              key={`parent-${feature.id}-${p.id}-slash`}
              as="span"
              color="dimmed"
              flex="0 0 auto"
              fontWeight="normal"
            >
              /
            </Box>,
          ])
        : null}
      <Box as="span" flex="0 1 auto" isTruncated>
        <HStack spacing={2}>
          <Text>{featureName}</Text>
          {feature.isArchived && showArchivedLabel && (
            <Text color="dimmed" {...archivedLabelProps}>
              {"(archived)"}
            </Text>
          )}
        </HStack>
      </Box>
    </Flex>
  );
}

export function FeatureIndicatorsDisplay({
  feature,
  showAutoFeedbackSurveyIndicator = false,
  showStaleIndicator = false,
  autoFeedbackSurveyLink = false,
  iconSize = "md",
}: {
  feature: FeatureDisplayProps["feature"];
  showAutoFeedbackSurveyIndicator?: boolean;
  autoFeedbackSurveyLink?: boolean;
  showStaleIndicator?: boolean;
  iconSize?: IconSizesType;
}) {
  const highlightIconColor = useColorModeValue("brand.500", "brand.400");
  const { env } = useEnvironment();
  const autoFeedbackContent = (
    <IcoAutoFeedbackSurveys
      aria-hidden="true"
      color={highlightIconColor}
      size={iconSize}
    />
  );
  return (
    <Flex alignItems="center" gap="2">
      <>
        {showAutoFeedbackSurveyIndicator &&
          feature.autoFeedbackSurveysEnabled && (
            <Tooltip
              label="Automated feedback surveys are enabled"
              maxW="52"
              textAlign="center"
              hasArrow
            >
              {autoFeedbackSurveyLink ? (
                <Link
                  as={RouterLink}
                  to={FeatureSettingsUrl(env, feature, "feedback")}
                >
                  {autoFeedbackContent}
                </Link>
              ) : (
                <Box flexShrink={0}>{autoFeedbackContent}</Box>
              )}
            </Tooltip>
          )}
      </>
      <>
        {showStaleIndicator && feature.isStale && !feature.isArchived && (
          <Tooltip
            label={
              feature.hasCleanupPr
                ? "Flag is stale - AI clean-up PR created or merged"
                : "Flag is stale - consider archiving or mark it as permanent"
            }
            maxW="52"
            textAlign="center"
            hasArrow
          >
            <Box flexShrink={0}>
              {feature.hasCleanupPr ? (
                <IcoFeatureAutoCleanup
                  aria-hidden="true"
                  color={highlightIconColor}
                  size={iconSize}
                />
              ) : (
                <IcoFeatureCleanup
                  aria-hidden="true"
                  color="dimmed"
                  size={iconSize}
                />
              )}
            </Box>
          </Tooltip>
        )}
      </>
    </Flex>
  );
}
